import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, User, Calendar, Tag, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import articles from '../data/articlesComplete';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';

const ArticleDetail = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const article = articles.find(a => a.slug === slug);
  
  // Find current article index and get prev/next
  const currentIndex = articles.findIndex(a => a.slug === slug);
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;

  useEffect(() => {
    setTimeout(() => setLoading(false), 500);
  }, []);

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading article..." />;
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-black text-white pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
          <p className="text-gray-400 mb-8">The article you are looking for does not exist.</p>
          <Link
            to="/articles"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Articles</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${article.title} | GENMOZPRO`}
        description={article.metaDescription}
        keywords={article.keywords}
      />
      
      <article className="min-h-screen bg-black text-white pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link
            to="/articles"
            className="inline-flex items-center space-x-2 text-gray-400 hover:text-yellow-500 transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Articles</span>
          </Link>

          {/* Article Header */}
          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-yellow-500/10 text-yellow-500 text-sm font-medium rounded-full">
                {article.category}
              </span>
              {article.tags.map((tag, index) => (
                <span key={index} className="flex items-center space-x-1 text-gray-400 text-sm">
                  <Tag className="w-3 h-3" />
                  <span>{tag}</span>
                </span>
              ))}
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm mb-6">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>{article.author}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(article.date).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>{article.readTime} read</span>
              </div>
            </div>

            <p className="text-xl text-gray-300 leading-relaxed">
              {article.excerpt}
            </p>
          </div>

          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Article Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold mb-4">Introduction</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Welcome to our comprehensive guide on {article.title.toLowerCase()}. In this article, we will explore the key concepts, best practices, and real-world applications that every developer should know in 2026.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Whether you are just starting out or looking to deepen your expertise, this guide provides actionable insights and detailed explanations to help you master this essential topic in payment testing and development.
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold mb-4">Key Concepts</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Understanding the fundamentals is crucial for successful implementation. Here are the core concepts you need to know:
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-3">
                  <span className="text-yellow-500 mt-1">•</span>
                  <span><strong>Foundation:</strong> Master the basic principles and understand how they apply to real-world scenarios in payment processing.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-yellow-500 mt-1">•</span>
                  <span><strong>Implementation:</strong> Learn practical approaches for integrating these concepts into your development workflow.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-yellow-500 mt-1">•</span>
                  <span><strong>Best Practices:</strong> Follow industry-standard guidelines to ensure security, reliability, and performance.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-yellow-500 mt-1">•</span>
                  <span><strong>Common Pitfalls:</strong> Avoid typical mistakes that developers encounter and learn from expert solutions.</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold mb-4">Why This Matters</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                In today's rapidly evolving payment landscape, staying current with best practices is essential. This topic is particularly important because:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-black/50 border border-gray-700 rounded-lg p-4">
                  <h3 className="text-yellow-500 font-semibold mb-2">Security</h3>
                  <p className="text-gray-400 text-sm">Protect sensitive payment data and maintain compliance with industry standards.</p>
                </div>
                <div className="bg-black/50 border border-gray-700 rounded-lg p-4">
                  <h3 className="text-yellow-500 font-semibold mb-2">Efficiency</h3>
                  <p className="text-gray-400 text-sm">Streamline your development process and reduce time-to-market.</p>
                </div>
                <div className="bg-black/50 border border-gray-700 rounded-lg p-4">
                  <h3 className="text-yellow-500 font-semibold mb-2">Reliability</h3>
                  <p className="text-gray-400 text-sm">Build robust systems that handle edge cases and errors gracefully.</p>
                </div>
                <div className="bg-black/50 border border-gray-700 rounded-lg p-4">
                  <h3 className="text-yellow-500 font-semibold mb-2">Scalability</h3>
                  <p className="text-gray-400 text-sm">Design solutions that grow with your business needs.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold mb-4">Practical Implementation</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Let's dive into practical examples and implementation strategies. Here's how you can apply these concepts in your projects:
              </p>
              
              <div className="bg-black rounded-lg p-6 border border-gray-700 mb-4">
                <h3 className="text-lg font-semibold mb-3 text-cyan-400">Step 1: Planning</h3>
                <p className="text-gray-300 mb-3">
                  Before implementation, take time to plan your approach. Consider your specific use case, requirements, and constraints. Document your architecture and data flow.
                </p>
                <div className="bg-gray-900 rounded p-4 font-mono text-sm text-gray-300">
                  <div className="text-green-400">// Example: Define your requirements</div>
                  <div>const requirements = &#123;</div>
                  <div className="ml-4">security: 'PCI-DSS compliant',</div>
                  <div className="ml-4">performance: 'sub-100ms response',</div>
                  <div className="ml-4">scalability: '1000+ req/sec'</div>
                  <div>&#125;;</div>
                </div>
              </div>

              <div className="bg-black rounded-lg p-6 border border-gray-700 mb-4">
                <h3 className="text-lg font-semibold mb-3 text-cyan-400">Step 2: Implementation</h3>
                <p className="text-gray-300 mb-3">
                  Follow best practices during implementation. Use proper error handling, validation, and security measures at every step.
                </p>
              </div>

              <div className="bg-black rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-3 text-cyan-400">Step 3: Testing</h3>
                <p className="text-gray-300">
                  Thoroughly test your implementation using tools like GENMOZPRO. Validate edge cases, error scenarios, and performance under load.
                </p>
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-bold mb-3 flex items-center space-x-2">
                <span className="text-blue-400">💡</span>
                <span>Pro Tips</span>
              </h3>
              <ul className="space-y-2 text-gray-300">
                <li>• Always use test environments before deploying to production</li>
                <li>• Keep your dependencies updated to latest stable versions</li>
                <li>• Monitor and log all critical operations for debugging</li>
                <li>• Follow security best practices and compliance requirements</li>
              </ul>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold mb-4">Conclusion</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Mastering {article.title.toLowerCase()} is essential for modern payment system development. By following the practices and principles outlined in this guide, you will be well-equipped to build secure, efficient, and scalable payment solutions.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Continue learning and stay updated with the latest developments in 2026. Use professional tools like GENMOZPRO to test and validate your implementations, ensuring the highest quality in your payment processing systems.
              </p>
            </div>
          </div>

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold">Share this article</h3>
              <button className="flex items-center space-x-2 px-6 py-3 bg-gray-900 border border-gray-800 rounded-lg hover:border-yellow-500 transition-colors">
                <Share2 className="w-5 h-5" />
                <span>Share</span>
              </button>
            </div>
          </div>

          {/* Next/Previous Navigation */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {prevArticle ? (
                <Link
                  to={`/articles/${prevArticle.slug}`}
                  className="group flex items-center space-x-3 px-6 py-4 bg-gray-900 border border-gray-800 rounded-lg hover:border-yellow-500 transition-all w-full sm:w-auto"
                >
                  <ChevronLeft className="w-5 h-5 text-yellow-500" />
                  <div className="text-left">
                    <p className="text-xs text-gray-500 mb-1">Previous Article</p>
                    <p className="font-semibold group-hover:text-yellow-500 transition-colors line-clamp-1">
                      {prevArticle.title}
                    </p>
                  </div>
                </Link>
              ) : (
                <div className="w-full sm:w-auto"></div>
              )}

              {nextArticle ? (
                <Link
                  to={`/articles/${nextArticle.slug}`}
                  className="group flex items-center space-x-3 px-6 py-4 bg-gray-900 border border-gray-800 rounded-lg hover:border-yellow-500 transition-all w-full sm:w-auto"
                >
                  <div className="text-right">
                    <p className="text-xs text-gray-500 mb-1">Next Article</p>
                    <p className="font-semibold group-hover:text-yellow-500 transition-colors line-clamp-1">
                      {nextArticle.title}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-yellow-500" />
                </Link>
              ) : (
                <div className="w-full sm:w-auto"></div>
              )}
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles.filter(a => a.id !== article.id && a.category === article.category).slice(0, 3).map(related => (
                <Link
                  key={related.id}
                  to={`/articles/${related.slug}`}
                  className="group bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-yellow-500 transition-all"
                >
                  <img src={related.image} alt={related.title} className="w-full h-40 object-cover" />
                  <div className="p-4">
                    <h4 className="font-semibold mb-2 group-hover:text-yellow-500 transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                    <p className="text-sm text-gray-400">{related.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default ArticleDetail;
